/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*/
import { MatDialog } from "@angular/material/dialog";
import { MyInjector } from "@src/app/app.module";
import { uuid } from "@src/app/model/abstract-object";
import { Txn } from "@src/app/model/txn";
import { Unit } from "@src/app/model/unit";
import { ConfirmDialogService } from "@src/app/shared/dialogs/confirmDialog";
import { FieldSetDialogComponent, FieldSetDialogOptions } from "@src/app/shared/dialogs/field-set-dialog/field-set-dialog.component";
import { FieldSet, LAYOUT_OPTIONS } from "@src/app/shared/form/field-set/field-set.component";
import { FormNumberComponent } from "@src/app/shared/form/form-number/form-number.component";
import { NrHelper } from "@src/app/shared/NrHelper";
import { maxValue } from "@src/app/shared/validators";
import { BankInService } from "@src/app/modules/txn/bank-in.service";
import { map } from "rxjs/operators";

export class PaymentDistributor {
    cds = MyInjector.instance.get(ConfirmDialogService);
    dialog = MyInjector.instance.get(MatDialog);
    bankInSvc = MyInjector.instance.get(BankInService);

    constructor(private amount: number, private personId : uuid, private units: Unit[] = [],
    private allocatorFn: (inv: Txn, unit: Unit, personId: uuid, maxAllocation: number) => void) {
        console.log('Distribute Payments');
        if (this.checkCanDistribute()) {
            this.distribute();
        }

    }

    checkCanDistribute(): boolean {
        if (!this.amount) {
            const title = $localize`No amount to distribute`;
            const msg = $localize`Enter an amount before distributing`;
            this.cds.alert(title, msg);
            return false;
        }
        if (this.units.length === 0) {
            const title = $localize`No units to distribute over`;
            const msg = $localize`Select a contact with associated units, or select a unit to distribute payment over`;
            this.cds.alert(title, msg);
            return false;
        }
        return true;
    }

    doDistributions(amount: {credit: number}) {
        return this.bankInSvc.getPersonOutstanding(this.personId).pipe(map( (outstanding => {
            if (outstanding) {
                outstanding.forEach( inv => {
                    const unit = this.units.find( u => u.id === inv.unitId);
                    if (unit) {
                        this.allocatorFn(inv, unit, this.personId, amount.credit);
                    }
                });
            }
            return outstanding;
        })));
    }

    distribute() {

        const each = Math.floor(this.amount / this.units.length * 100) / 100;
        const amtField = FormNumberComponent.makeCurrency('Amount to allocate to each unit', 'credit', {
            validators: [ maxValue(this.amount) ]
        });

        const fieldSet = new FieldSet({
            fields: [
                amtField,
            ],
            formLayout: LAYOUT_OPTIONS.simpleDialog
        })
        fieldSet.setValue({ credit: each } as Txn)
        const data = {
            fieldSet,
            saveFn: this.doDistributions.bind(this),
            saveText: $localize`Distribute`,
            title: $localize`Allocate payment of ${NrHelper.toFormattedCurrency(this.amount)} over ${this.units.length} units`,
            minWidth: '700px',
        } as FieldSetDialogOptions

        this.dialog.open(FieldSetDialogComponent, { data });
    }
}
